import { useCallback, useMemo } from "react";
import {
  selectLocalPeerID,
  selectPeerMetadata,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";

/**
 * The local peer's flags, shared with the room as peer metadata: hand raised,
 * be right back, and whether the interview panel is open (`isInterviewOn`,
 * open unless set to false). The store is the only source of truth, so every
 * control showing a flag agrees with the others.
 */
export function useMyMetadata() {
  const hmsActions = useHMSActions();
  const localPeerId = useHMSStore(selectLocalPeerID);
  const storedMetadata = useHMSStore(selectPeerMetadata(localPeerId));
  const metadata = useMemo(() => storedMetadata || {}, [storedMetadata]);
  const isHandRaised = Boolean(metadata.isHandRaised);
  const isBRBOn = Boolean(metadata.isBRBOn);
  const isInterviewOn = metadata.isInterviewOn ?? true;

  const update = useCallback(
    async fields => {
      try {
        await hmsActions.changeMetadata({ ...metadata, ...fields });
        return true;
      } catch (error) {
        console.error("failed to update metadata", error);
        return false;
      }
    },
    [hmsActions, metadata]
  );

  const toggleHandRaise = useCallback(
    () =>
      update({
        isHandRaised: !isHandRaised,
        isBRBOn: isHandRaised ? isBRBOn : false,
      }),
    [update, isHandRaised, isBRBOn]
  );

  const toggleBRB = useCallback(
    () =>
      update({
        isBRBOn: !isBRBOn,
        isHandRaised: isBRBOn ? isHandRaised : false,
      }),
    [update, isHandRaised, isBRBOn]
  );

  const toggleInterview = useCallback(
    () => update({ isInterviewOn: !isInterviewOn }),
    [update, isInterviewOn]
  );

  return {
    isHandRaised,
    isBRBOn,
    isInterviewOn,
    toggleHandRaise,
    toggleBRB,
    toggleInterview,
  };
}
